import { useCallback, useEffect, useState } from "react";
import {
  fetchBuyerFlowProgress,
  fetchBuyerSession,
  fetchEscrowPaymentRequest,
  listPublishedFiles,
  listReviews,
  notifyBuyerInvite,
  persistEscrowPaymentRequest,
  STORAGE_KEY
} from "../api/client";
import type { PublishedFileSummary } from "../domain/publishedFiles";
import { BF001, flowEvidenceFor } from "../domain/buyerFundingFlowSmoke";
import {
  paymentRequestInviteEmail,
  paymentRequestInviteEmailPlainText,
  paymentRequestInviteSms
} from "../domain/paymentRequestInvite";
import {
  buyerFundingLink,
  recordEscrowPaymentRequest,
  type BuyerFlowProgressSnapshot,
  type EscrowPaymentRequestRecord
} from "../domain/buyerFlowProgress";
import { formatMoney } from "../domain/money";
import type { BuyerFundingFlowSession } from "../flows/buyerFunding/types";
import { VetoPageShell } from "./VetoPageShell";
import { OfficePublishForm } from "./OfficePublishForm";

type TaskRow = {
  id: string;
  label: string;
  detail: string;
  status: "Not started" | "In progress" | "Recorded";
};

function buildTasks(progress?: BuyerFlowProgressSnapshot, requestSent?: boolean): TaskRow[] {
  return [
    {
      id: "request",
      label: "Secure payment request",
      detail: requestSent ? "Link sent to buyer" : "Send the buyer link from this file",
      status: requestSent ? "Recorded" : "Not started"
    },
    {
      id: "device",
      label: "Device check",
      detail: progress?.deviceVerified ? "Phone check recorded" : "Waiting on buyer",
      status: progress?.deviceVerified ? "Recorded" : progress?.step && progress.step !== "invite" ? "In progress" : "Not started"
    },
    {
      id: "identity",
      label: "Identity evidence",
      detail: progress?.identityRecorded ? "Identity step recorded" : "Waiting on buyer",
      status: progress?.identityRecorded ? "Recorded" : progress?.deviceVerified ? "In progress" : "Not started"
    },
    {
      id: "instructions",
      label: "Funding instructions",
      detail: progress?.instructionsDownloaded ? "Buyer downloaded funding instructions" : "Published on file",
      status: progress?.instructionsDownloaded ? "Recorded" : progress?.identityRecorded ? "In progress" : "Not started"
    },
    {
      id: "plaid",
      label: "Source account evidence",
      detail: progress?.plaidRecorded
        ? "Plaid or manual source recorded"
        : "Optional until buyer connects bank",
      status: progress?.plaidRecorded ? "Recorded" : progress?.instructionsDownloaded ? "In progress" : "Not started"
    },
    {
      id: "initiation",
      label: "Payment initiation",
      detail: progress?.paymentInitiated ? "Buyer reported funds sent" : "Waiting on buyer bank",
      status: progress?.paymentInitiated ? "Recorded" : progress?.instructionsDownloaded ? "In progress" : "Not started"
    },
    {
      id: "ack",
      label: "Buyer acknowledgment",
      detail: progress?.acknowledged ? "Known-entry acknowledgment recorded" : "Required before office reliance",
      status: progress?.acknowledged ? "Recorded" : progress?.paymentInitiated ? "In progress" : "Not started"
    }
  ];
}

function statusClass(status: TaskRow["status"]): string {
  if (status === "Recorded") return "text-[#191919] font-bold";
  if (status === "In progress") return "text-[#1f6b57] font-bold";
  return "text-[#596158]";
}

export function EscrowFileDashboard({
  fileCode = BF001.fileCode,
  onSelectFile
}: {
  fileCode?: string;
  onSelectFile?: (code: string) => void;
}) {
  const [session, setSession] = useState<BuyerFundingFlowSession | undefined>();
  const [sessionLoading, setSessionLoading] = useState(true);
  const [publishedFiles, setPublishedFiles] = useState<PublishedFileSummary[]>([]);
  const [progress, setProgress] = useState<BuyerFlowProgressSnapshot | undefined>();
  const [request, setRequest] = useState<EscrowPaymentRequestRecord | undefined>();
  const [notice, setNotice] = useState("");
  const [reviewLoaded, setReviewLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setSessionLoading(true);
      const loaded = await fetchBuyerSession(fileCode);
      if (cancelled) return;
      setSession(loaded?.session);
      setSessionLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [fileCode]);

  useEffect(() => {
    void listPublishedFiles()
      .then(setPublishedFiles)
      .catch(() => setPublishedFiles([]));
  }, [fileCode]);

  const refresh = useCallback(async () => {
    const [nextProgress, nextRequest] = await Promise.all([
      fetchBuyerFlowProgress(fileCode),
      fetchEscrowPaymentRequest(fileCode)
    ]);
    setProgress(nextProgress);
    setRequest(nextRequest);
  }, [fileCode]);

  useEffect(() => {
    void refresh();
    const onStorage = (event: StorageEvent) => {
      if (
        event.key === "buyer-funding-flow-progress" ||
        event.key === STORAGE_KEY ||
        event.key === "buyer-funding-payment-requests"
      ) {
        void refresh();
      }
    };
    window.addEventListener("storage", onStorage);
    const interval = window.setInterval(refresh, 2000);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.clearInterval(interval);
    };
  }, [refresh]);

  useEffect(() => {
    listReviews().then((reviews) => {
      const match = reviews.find((r) => r.fileNumber === fileCode || r.id === BF001.reviewId);
      setReviewLoaded(Boolean(match && flowEvidenceFor(match)?.buyerAcknowledgedAt));
    });
  }, [fileCode, progress?.acknowledged]);

  if (sessionLoading) {
    return (
      <VetoPageShell eyebrow="Escrow office" title="Loading file…">
        <section className="panel text-sm text-[#596158]">Loading {fileCode}…</section>
      </VetoPageShell>
    );
  }

  if (!session) {
    return (
      <VetoPageShell eyebrow="Escrow office" title="File not found">
        <OfficePublishForm
          onPublished={(code) => {
            onSelectFile?.(code);
          }}
        />
        <section className="panel mt-4">No published file for {fileCode}.</section>
      </VetoPageShell>
    );
  }

  const buyerLink = buyerFundingLink(fileCode);
  const tasks = buildTasks(progress, Boolean(request));
  const amount = formatMoney(session.amountDue);

  async function sendPaymentRequest() {
    const record = {
      fileCode: session!.fileCode,
      requestId: session!.paymentRequest.requestId,
      sentAt: new Date().toISOString(),
      sentBy: "Sample Escrow Officer",
      buyerLink,
      channel: "email_and_sms" as const
    };
    recordEscrowPaymentRequest(record);
    await persistEscrowPaymentRequest(record);
    void refresh();
    const smsMessage = paymentRequestInviteSms(
      session!.officeName,
      session!.paymentRequest.purpose,
      amount,
      session!.fileCode,
      buyerLink
    );
    const delivered = await notifyBuyerInvite(session!.fileCode, {
      buyerLink,
      message: smsMessage,
      sentAt: record.sentAt
    });
    const parts: string[] = [];
    if (delivered.email.sent) parts.push("invite email sent");
    if (delivered.sms.sent) parts.push("invite SMS sent");
    if (parts.length) {
      setNotice(`Payment request recorded. ${parts.join(" and ")}.`);
      return;
    }
    const errors = [delivered.email.error, delivered.sms.error].filter(Boolean).join(" ");
    setNotice(
      `Payment request recorded. ${errors || "Notifications were not sent."} Copy the buyer link or email draft if needed.`
    );
  }

  async function copyBuyerLink() {
    await navigator.clipboard.writeText(buyerLink);
    setNotice("Buyer link copied.");
  }

  async function copyEmailDraft() {
    const email = paymentRequestInviteEmail(session!, buyerLink, session!.buyerEmail);
    await navigator.clipboard.writeText(paymentRequestInviteEmailPlainText(email));
    setNotice("Email draft copied (matches buyer invite preview).");
  }

  return (
    <VetoPageShell
      eyebrow={session.officeName}
      title={session.propertyAddress}
      fileMeta={`File ${session.fileCode} · Close ${session.closingDate || "TBD"} · Due ${amount}`}
      darkHeader
      actions={
        <>
          <button className="button-primary border-white bg-white text-[#191919]" type="button" data-testid="escrow-send-request" onClick={sendPaymentRequest}>
            Send payment request
          </button>
          <a className="button-secondary border-[#d8d2c4] bg-transparent text-white" href={buyerLink} target="_blank" rel="noreferrer">
            Open buyer link
          </a>
          <a className="button-secondary border-[#d8d2c4] bg-transparent text-white" href="/office/review">
            Open Buyer Funding Review
          </a>
        </>
      }
    >
      {notice ? (
        <p className="panel text-sm" data-testid="escrow-notice">
          {notice}
        </p>
      ) : null}

      <OfficePublishForm
        onPublished={(code) => {
          onSelectFile?.(code);
        }}
      />

      {publishedFiles.length > 0 ? (
        <section className="panel mt-4">
          <p className="label">Published files</p>
          <ul className="mt-2 space-y-2 text-sm">
            {publishedFiles.map((file) => (
              <li key={file.fileCode}>
                <button
                  type="button"
                  className={`text-left underline-offset-2 hover:underline ${file.fileCode === fileCode ? "font-bold" : ""}`}
                  onClick={() => onSelectFile?.(file.fileCode)}
                >
                  {file.fileCode} — {file.buyerName} · ${file.amountDueLookup} · {file.purpose}
                </button>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <div className="grid gap-4 lg:grid-cols-[1fr_1fr]">
        <section className="panel" data-testid="escrow-client-contacts">
          <p className="label">Client contacts</p>
          <div className="mt-3 rounded-md border border-[#d8d2c4] p-4">
            <p className="font-bold">{session.buyerName}</p>
            <p className="text-sm text-[#596158]">Buyer · {session.invitePhoneMasked}</p>
            <p className="mt-2 text-sm">
              Purpose: {session.paymentRequest.purpose} · Request {session.paymentRequest.requestId}
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              <button className="button-secondary" type="button" onClick={copyBuyerLink}>
                Copy buyer link
              </button>
              <button className="button-secondary" type="button" onClick={copyEmailDraft}>
                Copy email draft
              </button>
            </div>
          </div>
        </section>

        <section className="panel" data-testid="escrow-buyer-progress">
          <p className="label">Buyer progress</p>
          <p className="mt-1 text-sm text-[#596158]">
            {progress
              ? `Last activity ${new Date(progress.updatedAt).toLocaleString()} · step ${progress.step}`
              : "No buyer activity in this browser yet."}
          </p>
          {reviewLoaded ? (
            <p className="notice mt-2 text-sm font-semibold text-[#1f6b57]">
              Buyer acknowledgment is on the office review. Export the receipt when your checks are complete.
            </p>
          ) : null}
        </section>
      </div>

      <section className="panel" data-testid="escrow-task-list">
        <p className="label">File tasks</p>
        <table className="data-table mt-3">
          <thead>
            <tr>
              <th>Task</th>
              <th>Detail</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {tasks.map((task) => (
              <tr key={task.id}>
                <td className="font-bold">{task.label}</td>
                <td>{task.detail}</td>
                <td className={statusClass(task.status)}>{task.status}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section className="panel">
        <p className="label">Payments on file</p>
        <p className="mt-2 text-sm text-[#596158]">
          Veto does not move money or mark funds received. Record trust receipt and four-way match in Buyer Funding
          Review.
        </p>
        <dl className="mt-4 grid gap-2 text-sm sm:grid-cols-2">
          <div>
            <dt className="font-bold">Amount due</dt>
            <dd>{amount}</dd>
          </div>
          <div>
            <dt className="font-bold">Rail</dt>
            <dd>{session.instructions.paymentRail}</dd>
          </div>
          <div>
            <dt className="font-bold">Initiation</dt>
            <dd>{progress?.paymentInitiated ? "Buyer reported" : "Not recorded"}</dd>
          </div>
          <div>
            <dt className="font-bold">Inbound receipt</dt>
            <dd>{reviewLoaded ? "On review — office to match" : "Not on review yet"}</dd>
          </div>
        </dl>
      </section>

      <section className="panel bg-[#fffdf8]">
        <p className="label">Instruction version</p>
        <p className="mt-2 text-sm">
          Version {session.instructions.versionLabel} · Beneficiary {session.instructions.beneficiaryName} · Bank{" "}
          {session.instructions.bankName} · Routing ···{session.instructions.routingLast4} · Account ···
          {session.instructions.accountLast4}
        </p>
      </section>
    </VetoPageShell>
  );
}
